import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { Skeleton } from "@/components/Skeleton";
import { Button } from "@/components/Button";
import {
  throughput,
  rollingAverage,
  leadTimes,
  average,
  completionTrend,
  createdVsCompleted,
  burnUp,
  percentile,
  projectedCompletion,
  cycleTimes,
  leadCycleByWeek,
} from "@/lib/reports";
import { toCsv, downloadCsv } from "@/lib/taskTableViews";
import { typeLabel } from "@/lib/workItemType";

const WEEKS = 10;
const SERIES_COLORS = [
  "#2563eb",
  "#16a34a",
  "#dc2626",
  "#d97706",
  "#7c3aed",
  "#0891b2",
  "#db2777",
  "#65a30d",
];

function shortWeek(key: string) {
  const [, m, d] = key.split("-");
  return `${m}/${d}`;
}

function ReportsView() {
  const { projectId } = Route.useParams();
  const navigate = useNavigate();
  const [breakdown, setBreakdown] = useState<"none" | "assignee" | "type">(
    "none",
  );

  const tasksQ = useQuery({
    queryKey: queryKeys.tasksFor(projectId),
    queryFn: () => api.listTasks(projectId),
  });
  const teamQ = useQuery({ queryKey: queryKeys.team(), queryFn: api.listTeam });
  const names = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of teamQ.data?.members ?? []) m.set(u.id, u.name ?? u.email);
    return m;
  }, [teamQ.data]);

  const tasks = tasksQ.data?.tasks ?? [];
  const tp = useMemo(
    () => throughput(tasks, WEEKS, breakdown),
    [tasks, breakdown],
  );
  const totalsPerWeek = tp.weeks.map((_, i) =>
    tp.series.reduce((sum, s) => sum + s.counts[i]!, 0),
  );
  const avgLine = rollingAverage(totalsPerWeek, 4);
  const maxY = Math.max(1, ...totalsPerWeek);
  const lead = leadTimes(tasks);
  const trend = useMemo(() => completionTrend(tasks, WEEKS), [tasks]);
  const cvc = useMemo(() => createdVsCompleted(tasks, WEEKS), [tasks]);
  const recentTp = average(totalsPerWeek.slice(-4));
  const projection = useMemo(
    () => projectedCompletion(tasks, recentTp),
    [tasks, recentTp],
  );
  const cvcMax = Math.max(1, ...cvc.created, ...cvc.completed);
  const bu = useMemo(() => burnUp(tasks, WEEKS), [tasks]);
  const buMax = Math.max(1, ...bu.scope, ...bu.done);
  const cycles = useMemo(() => cycleTimes(tasks), [tasks]);
  const cycleDays = cycles.map((c) => c.days);
  const cycleP85 = percentile(cycleDays, 85);
  const lcw = useMemo(() => leadCycleByWeek(tasks, WEEKS), [tasks]);
  const lcMax = Math.max(1, ...lcw.lead, ...lcw.cycle);

  const seriesLabel = (key: string) =>
    breakdown === "assignee"
      ? (names.get(key) ?? key)
      : breakdown === "type"
        ? typeLabel(key as "task")
        : key;

  function exportCsv() {
    const headers = [
      "week",
      ...tp.series.map((s) => seriesLabel(s.key)),
      "rolling_avg",
    ];
    const rows = tp.weeks.map((w, i) => [
      w,
      ...tp.series.map((s) => String(s.counts[i])),
      avgLine[i]!.toFixed(2),
    ]);
    downloadCsv(`throughput-${projectId}.csv`, toCsv(headers, rows));
  }

  // Chart geometry
  const W = 680;
  const H = 220;
  const padL = 28;
  const padB = 26;
  const plotW = W - padL - 8;
  const plotH = H - padB - 10;
  const bw = plotW / tp.weeks.length;

  if (tasksQ.isLoading) {
    return (
      <div className="proj-shell-content">
        <Skeleton count={4} height={50} />
      </div>
    );
  }
  if (tasksQ.isError) {
    return (
      <div className="proj-shell-content">
        <p className="t-muted" style={{ padding: 24 }}>
          Couldn&rsquo;t load report data. Try again.
        </p>
      </div>
    );
  }

  return (
    <div className="proj-shell-content rpt-page">
      <section className="rpt-card">
        <div className="rpt-head">
          <h3 className="rpt-h">Throughput — tasks completed per week</h3>
          <div className="rpt-actions">
            <select
              className="select"
              value={breakdown}
              onChange={(e) =>
                setBreakdown(e.target.value as "none" | "assignee" | "type")
              }
              aria-label="Breakdown"
              style={{ width: 140 }}
            >
              <option value="none">No breakdown</option>
              <option value="assignee">By assignee</option>
              <option value="type">By type</option>
            </select>
            <Button variant="secondary" onClick={exportCsv}>
              Export CSV
            </Button>
          </div>
        </div>
        <svg
          className="rpt-chart"
          viewBox={`0 0 ${W} ${H}`}
          role="img"
          aria-label="Throughput chart"
        >
          {/* y gridlines */}
          {[0, 0.5, 1].map((f) => {
            const y = 10 + plotH * (1 - f);
            return (
              <g key={f}>
                <line x1={padL} y1={y} x2={W - 8} y2={y} className="rpt-grid" />
                <text x={4} y={y + 3} className="rpt-axis">
                  {Math.round(maxY * f)}
                </text>
              </g>
            );
          })}
          {/* stacked bars */}
          {tp.weeks.map((w, i) => {
            let acc = 0;
            return (
              <g key={w}>
                {tp.series.map((s, si) => {
                  const v = s.counts[i]!;
                  if (v === 0) return null;
                  const h = (v / maxY) * plotH;
                  const y = 10 + plotH - acc - h;
                  acc += h;
                  return (
                    <rect
                      key={s.key}
                      x={padL + i * bw + bw * 0.15}
                      y={y}
                      width={bw * 0.7}
                      height={h}
                      fill={SERIES_COLORS[si % SERIES_COLORS.length]}
                    >
                      <title>{`${seriesLabel(s.key)}: ${v}`}</title>
                    </rect>
                  );
                })}
                <text
                  x={padL + i * bw + bw / 2}
                  y={H - 8}
                  className="rpt-axis"
                  textAnchor="middle"
                >
                  {shortWeek(w)}
                </text>
              </g>
            );
          })}
          {/* rolling average line */}
          <polyline
            className="rpt-avg"
            points={avgLine
              .map(
                (v, i) =>
                  `${padL + i * bw + bw / 2},${10 + plotH - (v / maxY) * plotH}`,
              )
              .join(" ")}
          />
        </svg>
        {breakdown !== "none" && (
          <div className="rpt-legend">
            {tp.series.map((s, si) => (
              <span key={s.key} className="rpt-leg-item">
                <span
                  className="rpt-swatch"
                  style={{
                    background: SERIES_COLORS[si % SERIES_COLORS.length],
                  }}
                />
                {seriesLabel(s.key)}
              </span>
            ))}
          </div>
        )}
        <p className="t-sm t-muted">
          Line = 4-week rolling average.{" "}
          {totalsPerWeek.reduce((a, b) => a + b, 0)} tasks completed in the last{" "}
          {WEEKS} weeks.
        </p>
      </section>

      <section className="rpt-card">
        <h3 className="rpt-h">Created vs completed</h3>
        <svg
          className="rpt-chart"
          viewBox={`0 0 ${W} ${H}`}
          role="img"
          aria-label="Created versus completed chart"
        >
          {[0, 0.5, 1].map((f) => {
            const y = 10 + plotH * (1 - f);
            return (
              <g key={f}>
                <line x1={padL} y1={y} x2={W - 8} y2={y} className="rpt-grid" />
                <text x={4} y={y + 3} className="rpt-axis">
                  {Math.round(cvcMax * f)}
                </text>
              </g>
            );
          })}
          {(
            [
              ["created", cvc.created, "#d97706"],
              ["completed", cvc.completed, "#16a34a"],
            ] as const
          ).map(([id, vals, color]) => (
            <polyline
              key={id}
              fill="none"
              stroke={color}
              strokeWidth={2}
              points={vals
                .map(
                  (v, i) =>
                    `${padL + i * bw + bw / 2},${10 + plotH - (v / cvcMax) * plotH}`,
                )
                .join(" ")}
            />
          ))}
          {cvc.weeks.map((w, i) => (
            <text
              key={w}
              x={padL + i * bw + bw / 2}
              y={H - 8}
              className="rpt-axis"
              textAnchor="middle"
            >
              {shortWeek(w)}
            </text>
          ))}
        </svg>
        <div className="rpt-legend">
          <span className="rpt-leg-item">
            <span className="rpt-swatch" style={{ background: "#d97706" }} />
            Created
          </span>
          <span className="rpt-leg-item">
            <span className="rpt-swatch" style={{ background: "#16a34a" }} />
            Completed
          </span>
        </div>
        <p className="t-sm t-muted">
          {projection
            ? `At the recent rate (~${recentTp.toFixed(1)}/wk), the open backlog clears in ~${projection.weeks} weeks (≈ ${projection.date}).`
            : "Projected completion needs some recent throughput and open tasks."}
        </p>
      </section>

      <section className="rpt-card">
        <h3 className="rpt-h">Burn-up — scope vs completed</h3>
        <svg
          className="rpt-chart"
          viewBox={`0 0 ${W} ${H}`}
          role="img"
          aria-label="Burn-up chart: cumulative scope versus completed"
        >
          {[0, 0.5, 1].map((f) => {
            const y = 10 + plotH * (1 - f);
            return (
              <g key={f}>
                <line x1={padL} y1={y} x2={W - 8} y2={y} className="rpt-grid" />
                <text x={4} y={y + 3} className="rpt-axis">
                  {Math.round(buMax * f)}
                </text>
              </g>
            );
          })}
          {/* remaining-work band between scope and done */}
          <polygon
            fill="#2563eb"
            fillOpacity={0.08}
            points={[
              ...bu.scope.map(
                (v, i) =>
                  `${padL + i * bw + bw / 2},${10 + plotH - (v / buMax) * plotH}`,
              ),
              ...bu.done
                .map(
                  (v, i) =>
                    `${padL + i * bw + bw / 2},${10 + plotH - (v / buMax) * plotH}`,
                )
                .reverse(),
            ].join(" ")}
          />
          {(
            [
              ["scope", bu.scope, "#2563eb"],
              ["done", bu.done, "#16a34a"],
            ] as const
          ).map(([id, vals, color]) => (
            <polyline
              key={id}
              fill="none"
              stroke={color}
              strokeWidth={2}
              points={vals
                .map(
                  (v, i) =>
                    `${padL + i * bw + bw / 2},${10 + plotH - (v / buMax) * plotH}`,
                )
                .join(" ")}
            />
          ))}
          {/* scope-change markers: weeks where new scope entered */}
          {bu.scopeAdded.map((added, i) =>
            added > 0 ? (
              <circle
                key={i}
                cx={padL + i * bw + bw / 2}
                cy={10 + plotH - (bu.scope[i]! / buMax) * plotH}
                r={3.5}
                fill="#d97706"
              >
                <title>{`+${added} task${added === 1 ? "" : "s"} added (${shortWeek(bu.weeks[i]!)})`}</title>
              </circle>
            ) : null,
          )}
          {bu.weeks.map((w, i) => (
            <text
              key={w}
              x={padL + i * bw + bw / 2}
              y={H - 8}
              className="rpt-axis"
              textAnchor="middle"
            >
              {shortWeek(w)}
            </text>
          ))}
        </svg>
        <div className="rpt-legend">
          <span className="rpt-leg-item">
            <span className="rpt-swatch" style={{ background: "#2563eb" }} />
            Scope (total)
          </span>
          <span className="rpt-leg-item">
            <span className="rpt-swatch" style={{ background: "#16a34a" }} />
            Completed
          </span>
          <span className="rpt-leg-item">
            <span className="rpt-swatch" style={{ background: "#d97706" }} />
            Scope added
          </span>
        </div>
        <p className="t-sm t-muted">
          The gap between scope and completed is remaining work; amber dots mark
          weeks where new tasks entered scope.
        </p>
      </section>

      <div className="rpt-row">
        <section className="rpt-card rpt-stat">
          <h3 className="rpt-h">Lead time</h3>
          <div className="rpt-big">{average(lead).toFixed(1)}d</div>
          <p className="t-sm t-muted">
            Average creation → completion across {lead.length} completed task
            {lead.length === 1 ? "" : "s"}. P50{" "}
            {percentile(lead, 50).toFixed(0)}d · P85{" "}
            {percentile(lead, 85).toFixed(0)}d.
          </p>
        </section>
        <section className="rpt-card rpt-stat">
          <h3 className="rpt-h">Completion trend</h3>
          <div className="rpt-big">
            {trend.cumulative[trend.cumulative.length - 1] ?? 0}
          </div>
          <p className="t-sm t-muted">
            Cumulative completed over {WEEKS} weeks (
            {trend.cumulative.map((c) => c).join(" → ")}).
          </p>
        </section>
      </div>

      <section className="rpt-card">
        <h3 className="rpt-h">Lead vs cycle time (avg days/week)</h3>
        <svg
          className="rpt-chart"
          viewBox={`0 0 ${W} ${H}`}
          role="img"
          aria-label="Lead versus cycle time chart"
        >
          {[0, 0.5, 1].map((f) => {
            const y = 10 + plotH * (1 - f);
            return (
              <g key={f}>
                <line x1={padL} y1={y} x2={W - 8} y2={y} className="rpt-grid" />
                <text x={4} y={y + 3} className="rpt-axis">
                  {Math.round(lcMax * f)}
                </text>
              </g>
            );
          })}
          {(
            [
              ["lead", lcw.lead, "#2563eb"],
              ["cycle", lcw.cycle, "#dc2626"],
            ] as const
          ).map(([id, vals, color]) => (
            <polyline
              key={id}
              fill="none"
              stroke={color}
              strokeWidth={2}
              points={vals
                .map(
                  (v, i) =>
                    `${padL + i * bw + bw / 2},${10 + plotH - (v / lcMax) * plotH}`,
                )
                .join(" ")}
            />
          ))}
          {lcw.weeks.map((w, i) => (
            <text
              key={w}
              x={padL + i * bw + bw / 2}
              y={H - 8}
              className="rpt-axis"
              textAnchor="middle"
            >
              {shortWeek(w)}
            </text>
          ))}
        </svg>
        <div className="rpt-legend">
          <span className="rpt-leg-item">
            <span className="rpt-swatch" style={{ background: "#2563eb" }} />
            Lead time
          </span>
          <span className="rpt-leg-item">
            <span className="rpt-swatch" style={{ background: "#dc2626" }} />
            Cycle time
          </span>
        </div>
        <p className="t-sm t-muted">
          Lead (creation→done) sits above cycle (doing→done); the gap is queue
          time before work started.
        </p>
      </section>

      <section className="rpt-card">
        <h3 className="rpt-h">Cycle time (doing → done)</h3>
        {cycles.length === 0 ? (
          <p className="t-sm t-muted">
            No completed tasks have a recorded start yet. Move a task to Doing,
            then Done, to measure its cycle time.
          </p>
        ) : (
          <>
            <p className="t-sm t-muted" style={{ marginTop: 0 }}>
              Average {average(cycleDays).toFixed(1)}d · P85{" "}
              {cycleP85.toFixed(0)}d across {cycles.length} task
              {cycles.length === 1 ? "" : "s"}. Outliers (≥ P85) are flagged.
            </p>
            <ul className="rpt-outliers">
              {cycles.slice(0, 8).map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    className="rpt-outlier"
                    onClick={() =>
                      navigate({
                        to: "/tasks/$taskId",
                        params: { taskId: c.id },
                      })
                    }
                  >
                    <span
                      className={`rpt-bar-cell${c.days >= cycleP85 ? " hot" : ""}`}
                      style={{
                        width: `${Math.max(4, (c.days / (cycles[0]!.days || 1)) * 100)}%`,
                      }}
                    />
                    <span className="rpt-outlier-title">{c.title}</span>
                    <span className="t-sm t-muted">{c.days.toFixed(0)}d</span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
    </div>
  );
}

export const Route = createFileRoute("/projects/$projectId/reports")({
  component: ReportsView,
});
